import { useEffect, useRef, useState } from "react";
import { getEmbedViewport, subscribeEmbedViewport } from "../../helper/embedViewport";
import { onBookingBarAction, sendBookingBar } from "../../helper/parentBookingBar";

const PHONE_QUERY = "(max-width: 767px)";

const matchesPhone = () =>
  typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches;

const primaryColor = () =>
  window.getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim();

// Hands the booking bar of a phone to the page around the iframe when it
// shows one (see helper/parentBookingBar.js).
const useParentBookingBar = ({ visible, label, total, action, disabled, onAction }) => {
  const [parentBar, setParentBar] = useState(() => getEmbedViewport()?.bar === true);
  const [phone, setPhone] = useState(matchesPhone);
  // The colours of the tenant come in a stylesheet that may load later, so
  // the colour is read again when it loads and with each update of the page.
  const [color, setColor] = useState(primaryColor);
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;

  useEffect(
    () =>
      subscribeEmbedViewport((viewport) => {
        setParentBar(viewport.bar === true);
        setColor(primaryColor());
      }),
    []
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(PHONE_QUERY);
    const update = () => setPhone(query.matches);
    query.addEventListener("change", update);

    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => onBookingBarAction(() => onActionRef.current()), []);

  useEffect(() => {
    const onLoad = (event) => {
      if (event.target?.tagName === "LINK") setColor(primaryColor());
    };
    document.addEventListener("load", onLoad, true);

    return () => document.removeEventListener("load", onLoad, true);
  }, []);

  useEffect(() => {
    if (!parentBar) return;
    sendBookingBar({
      visible: visible && phone,
      label,
      total,
      action,
      disabled,
      color,
    });
  }, [parentBar, visible, phone, label, total, action, disabled, color]);

  // Leaving the page takes the bar away.
  useEffect(() => () => sendBookingBar({ visible: false }), []);

  return parentBar;
};

export default useParentBookingBar;
